import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  PanelRow,
  Icon,
  Panel,
  Drawer,
  PanelHeader,
  PanelBody,
  Button,
  Field,
  FieldLabel,
  Select,
  Modal,
  Loader,
} from '@teamsnap/teamsnap-ui';
import * as GetForm from 'core/gql/queries/show-form';
import * as UpdateRegistration from 'core/gql/mutations/update-registration';
import * as GqlOrganization from 'core/gql/queries/show-organizations';
import * as ListPaymentAccounts from 'core/gql/queries/list-payment-accounts';
import * as RegistrationFormAdjustments from 'core/gql/queries/registration-form-adjustments';
import * as Types from './types';
import * as GqlForms from 'core/gql/queries/get-all-forms-reg';
import { APP_URL, REGISTRANT_URL } from 'core/constants';
import EnhancedDateTimePicker from './EnhancedDateTimePicker';
import UnapprovedMerchantAccountModal from './UnapprovedMerchantAccountModal';
import {
  convertToUTC,
  getOrgTimezone,
  excludeDaysUntilStartDate,
  excludeCurrentDayPastTimes,
  excludeStartDatePastTimes,
  convertTimeToDisplayString,
} from './date-utils';
import { reactiveFeedbackMessage } from 'core/apollo-cache';

export interface Props {
  formIDForSharing: number;
  setFormIdForSharing: (boolean) => void;
  isShareDrawerOpen: boolean;
  setIsShareDrawerOpen: (boolean) => void;
  orgId: number;
}

const ShareFormDrawer: React.FunctionComponent<Props> = ({
  formIDForSharing,
  setFormIdForSharing,
  isShareDrawerOpen,
  setIsShareDrawerOpen,
  orgId,
}) => {
  const [copied, setCopied] = React.useState<boolean>(false);
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [status, setStatus] = React.useState<'open' | 'closed' | undefined>(
    undefined
  );
  const [isSaveDisabled, setIsSaveDisabled] = React.useState<boolean>(true);
  const [errorMessage, setErrorMessage] = React.useState<string>(undefined);
  const [errorDescriptor, setErrorDescriptor] =
    React.useState<string>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] =
    React.useState<boolean>(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = React.useState<boolean>(false);
  const [isUnapprovedAccountModalOpen, setIsUnapprovedAccountModalOpen] =
    React.useState<boolean>(false);
  const [dateUpdated, setDateUpdated] = React.useState<boolean>(false);
  const [currentPaymentAccountUnapproved, setCurrentPaymentAccountUnapproved] =
    React.useState<boolean>(false);
  const [currentPaymentAccountPaymentsEnabled, setCurrentPaymentAccountPaymentsEnabled] =
    React.useState<boolean>(false);

  const mutationOptions = {
    refetchQueries: [
      {
        query: GetForm.QUERY,
        variables: { formId: formIDForSharing, organizationId: +orgId },
      },
      { query: GqlForms.QUERY, variables: { organizationId: orgId } },
    ],
  };

  const [updateRegistration] = useMutation(
    UpdateRegistration.MUTATION,
    mutationOptions
  );

  const formAdjustments =
    useQuery(RegistrationFormAdjustments.QUERY, {
      variables: { formId: +formIDForSharing },
      skip: !formIDForSharing,
    })?.data?.registrationFormAdjustments || [];

  const { data } = useQuery(ListPaymentAccounts.QUERY, {
    variables: { scopeId: +orgId, scopeType: 'organization' },
    errorPolicy: 'all',
  });

  const orgDataQuery = useQuery(GqlOrganization.QUERY, {
    variables: { organizationId: orgId },
    skip: !orgId,
    errorPolicy: 'all',
  });

  const formattedTimezone = getOrgTimezone(
    orgDataQuery?.data?.showOrganization?.timezoneIana ?? '',
    orgDataQuery?.data?.showOrganization?.timezone ?? ''
  );

  const formForSharing =
    useQuery(GetForm.QUERY, {
      variables: { formId: formIDForSharing, organizationId: orgId },
      skip: !formIDForSharing,
    })?.data?.showRegistrationForm || null;

  const shareUrl = `${REGISTRANT_URL}/form/${formIDForSharing}`;

  const oneMinute = 60000;

  const clearStartDate = () => {
    setStartDate(null);

    if (endDate) {
      setEndDate(null);
    }
  };

  const clearEndDate = () => setEndDate(null);

  const changesInvoked = formForSharing
    ? formForSharing?.status !== status || dateUpdated
    : false;

  const handleUnapprovedAccountModalSubmit = () => {
    window.location.href = `${APP_URL}/organizations/${orgId}/registration/${formForSharing?.id}/${Types.RegistrationSteps.DEPOSIT_ACCOUNT}`;
  };

  const handleUnapprovedAccountModalClose = () => {
    setIsUnapprovedAccountModalOpen(false);

    if (!currentPaymentAccountPaymentsEnabled) {
      setStatus('closed');
      setStartDate(null);
      setEndDate(null);
      setDateUpdated(false);
    } else {
      handleSaveConfirmation();
      setIsShareDrawerOpen(false);
    }
  };

  const handleStartDateChange = (date: Date) => {
    const currentTime = new Date().getTime();
    const startTime = date.getTime();

    setStartDate(date);
    setDateUpdated(true);

    if (startTime > currentTime) {
      setStatus('closed');
    } else if (
      startTime <= currentTime &&
      (!endDate || endDate?.getTime() > currentTime)
    ) {
      setStatus('open');
    }
  };

  const handleEndDateChange = (date: Date) => {
    const currentTime = new Date().getTime();
    const end = date.getTime();

    if (startDate) {
      const start = startDate.getTime();
      const absoluteDiff = Math.abs(end - start);

      if (start === end) {
        setEndDate(new Date(date.getTime() + oneMinute));
      } else if (start > end) {
        const addTime = start - end + oneMinute;
        setEndDate(new Date(date.getTime() + addTime));
      } else if (absoluteDiff < oneMinute) {
        const addTime = oneMinute - absoluteDiff;
        setEndDate(new Date(date.getTime() + addTime));
      } else {
        setEndDate(date);
      }

      if (end <= currentTime) {
        setStatus('closed');
      } else if (startDate.getTime() <= currentTime && end > currentTime) {
        setStatus('open');
      } else if (startDate.getTime() > currentTime && end > currentTime) {
        setStatus('closed');
      } else if (startDate.getTime() >= currentTime && !end) {
        setStatus('open');
      }
    } else {
      setEndDate(date);
    }

    setDateUpdated(true);
  };

  const handleStatusChange = (status) => {
    const currentDate = new Date();
    const opened_at =
      formForSharing && formForSharing.opened_at
        ? new Date(formForSharing.opened_at)
        : undefined;
    const closed_at =
      formForSharing && formForSharing.closed_at
        ? new Date(formForSharing.closed_at)
        : undefined;

    setStatus(status);

    if (status === 'closed') {
      if (opened_at && closed_at) {
        if (opened_at.getTime() > currentDate.getTime()) {
          setStartDate(new Date(currentDate.getTime() - oneMinute));
        }

        setEndDate(currentDate);
      } else if (opened_at && opened_at.getTime() < currentDate.getTime()) {
        setEndDate(currentDate);
      } else {
        setStartDate(new Date(currentDate.getTime() - oneMinute));
        setEndDate(currentDate);
      }
    } else if (status === 'open') {
      if (opened_at && closed_at) {
        if (!opened_at && !closed_at) {
          setStartDate(currentDate);
          setEndDate(null);
        } else if (opened_at && closed_at) {
          if (
            opened_at.getTime() < currentDate.getTime() &&
            closed_at.getTime() < currentDate.getTime()
          ) {
            setStartDate(currentDate);
            setEndDate(null);
          } else if (
            opened_at.getTime() > currentDate.getTime() &&
            closed_at.getTime() > currentDate.getTime()
          ) {
            setStartDate(currentDate);
          } else {
            setStartDate(currentDate);
            setEndDate(null);
          }
        }
      } else if (!closed_at) {
        setStartDate(currentDate);
      }
    }

    setDateUpdated(true);
  };

  const handleCancel = () => {
    if (formForSharing) {
      if (changesInvoked) {
        setIsCancelModalOpen(true);
      } else {
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  const handleSave = () => {
    if (currentPaymentAccountUnapproved && status !== 'closed') {
      setIsUnapprovedAccountModalOpen(true);
    } else if (changesInvoked) {
      setIsSaveModalOpen(true);
    } else {
      sendUpdateRegistrationRequest();
    }
  };

  const handleSaveConfirmation = () => {
    sendUpdateRegistrationRequest();
    setIsSaveModalOpen(false);
    setDateUpdated(false);
  };

  const handleClose = () => {
    setIsCancelModalOpen(false);
    setStartDate(null);
    setEndDate(null);
    setIsShareDrawerOpen(false);
    setFormIdForSharing(null);
    setCopied(false);
    setStatus(undefined);
  };

  const cancelModalChildren = (
    <div className="shareform-cancel-modal-children-container">
      <div className="text-container">
        <p>You have unsaved changes.</p>
        <p>
          The changes you made will not be saved, are you sure you want to
          close?
        </p>
      </div>

      <div className="action-item-container">
        <Button
          style={{
            backgroundColor: '#f6f8fb',
            marginRight: 16,
          }}
          onClick={() => setIsCancelModalOpen(false)}
          mods="u-padSidesXl"
        >
          No, keep editing
        </Button>

        <Button
          mods="u-padSidesXxl u-bgNegative6"
          color="primary"
          onClick={handleClose}
        >
          Yes, close
        </Button>
      </div>
    </div>
  );

  const saveModalChildren = (
    <div className="shareform-cancel-modal-children-container">
      <div className="text-container">
        <p>
          Changes have been made. Old information will not be saved. Are you
          sure you want to continue?
        </p>
      </div>

      <div className="action-item-container">
        <Button
          style={{
            backgroundColor: '#f6f8fb',
            marginRight: 16,
          }}
          onClick={() => setIsSaveModalOpen(false)}
          mods="u-padSidesXl"
        >
          No, keep editing
        </Button>

        <Button
          mods="u-padSidesXxl"
          color="primary"
          onClick={handleSaveConfirmation}
        >
          Yes, continue
        </Button>
      </div>
    </div>
  );

  const sendUpdateRegistrationRequest = React.useCallback(async () => {
    setIsLoading(true);

    try {
      const {
        data: {
          showOrganization: { timezoneIana },
        },
      } = orgDataQuery;

      const opened = convertToUTC(startDate, timezoneIana);
      const closed = convertToUTC(endDate, timezoneIana);

      const response = await updateRegistration({
        variables: {
          id: formForSharing?.id,
          name: formForSharing?.name,
          seasonId: parseInt(formForSharing?.season_id),
          openedAt: opened,
          closedAt: closed,
        },
      });

      if (response?.data?.updateRegistrationForm?.id) {
        reactiveFeedbackMessage({
          message: 'Your changes have been saved!',
          isError: false,
          autoclose: true,
        });
      } else {
        reactiveFeedbackMessage({
          message:
            'Oops!  Something happened. Unable to update registration form.',
          isError: true,
          autoclose: true,
        });
      }

      setIsLoading(false);
    } catch (error) {
      if (error.graphQLErrors) {
        const [errors] = error.graphQLErrors;

        reactiveFeedbackMessage({
          message: errors.message,
          isError: true,
          autoclose: true,
        });
      } else {
        reactiveFeedbackMessage({
          message: 'Oops! Something happened. Please try again.',
          isError: true,
          autoclose: true,
        });
      }

      setIsLoading(false);
    }
  }, [endDate, formForSharing, orgDataQuery, startDate, updateRegistration]);

  React.useEffect(() => {
    if (data && data.listPaymentAccounts && formForSharing) {
      const paymentAccounts = data.listPaymentAccounts;
      const selectedAccount = paymentAccounts.find(
        (account) => account.id === formForSharing.paymentAccountId
      );
      const hasFormFees =
        formForSharing.participant_groups?.some(
          (group) => group.participant_fee > 0
        ) || formAdjustments?.some((adj) => Number(adj.amount) > 0);

      if (
        hasFormFees &&
        selectedAccount &&
        (selectedAccount.paymentsEnabled === false ||
          selectedAccount.payoutsEnabled === false)
      ) {
        setCurrentPaymentAccountUnapproved(true);
        setCurrentPaymentAccountPaymentsEnabled(selectedAccount.paymentsEnabled);
      } else {
        setCurrentPaymentAccountUnapproved(false);
        setCurrentPaymentAccountPaymentsEnabled(false);

      }
    }
  }, [data, formAdjustments, formForSharing]);

  React.useEffect(() => {
    const formatSavedDate = (savedForm) => {
      try {
        const {
          data: {
            showOrganization: { timezoneIana },
          },
        } = orgDataQuery;

        const { opened_at: openedAt, closed_at: closedAt } = savedForm;

        if (openedAt && closedAt) {
          const formattedOrgStartTime = convertTimeToDisplayString(
            openedAt,
            timezoneIana
          );

          const formattedOrgCloseTime = convertTimeToDisplayString(
            closedAt,
            timezoneIana
          );

          setStartDate(new Date(formattedOrgStartTime));
          setEndDate(new Date(formattedOrgCloseTime));

          return `${formattedOrgStartTime} - ${formattedOrgCloseTime}`;
        }

        if (openedAt && !closedAt) {
          const formattedOrgStartTime = convertTimeToDisplayString(
            openedAt,
            timezoneIana
          );

          setStartDate(new Date(formattedOrgStartTime));

          return `Opens ${formattedOrgStartTime}`;
        }

        return 'None Added';
      } catch (e) {
        console.error(`registration::formatSavedDate(): ${e}`);
      }
    };

    if (formForSharing) {
      setStatus(formForSharing.status);

      formatSavedDate(formForSharing);
    }
  }, [formForSharing, isShareDrawerOpen, orgDataQuery]);

  // The below useEffect() will track our validation (primarily around dates).
  React.useEffect(() => {
    if (!startDate && endDate) {
      setIsSaveDisabled(true);
      setErrorMessage(
        'Because you have entered a Closed Date and time, an Open Date and time is required to proceed.'
      );
      setErrorDescriptor('Cannot have only Closed Date.');
    } else if (endDate && startDate?.getTime() > endDate.getTime()) {
      setIsSaveDisabled(true);
      setErrorMessage('Closed Date must occur after the Open Date.');
      setErrorDescriptor('Closed Date is too early.');
    } else {
      setIsSaveDisabled(false);
      setErrorMessage(undefined);
    }
  }, [endDate, startDate]);

  React.useEffect(() => {
    // Reset form when drawer is closed.
    if (!isShareDrawerOpen) {
      setDateUpdated(false);
      setStatus(undefined);
      setEndDate(null);
      setStartDate(null);
    }
  }, [isShareDrawerOpen]);

  return (
    <>
      {isCancelModalOpen && (
        <Modal
          show={isCancelModalOpen}
          heading={'Unsaved Changes'}
          children={cancelModalChildren}
          style={{ padding: '1.5rem' }}
        />
      )}

      {isSaveModalOpen && (
        <Modal
          show={isSaveModalOpen}
          heading={'Save Changes'}
          children={saveModalChildren}
          style={{ padding: '1.5rem' }}
        />
      )}

      {isUnapprovedAccountModalOpen && (
        <UnapprovedMerchantAccountModal
          isOpen={isUnapprovedAccountModalOpen}
          paymentsEnabled={currentPaymentAccountPaymentsEnabled}
          closeFn={handleUnapprovedAccountModalClose}
          onSubmit={handleUnapprovedAccountModalSubmit}
          allowSave={currentPaymentAccountPaymentsEnabled}
        />
      )}

      <Drawer
        open={isShareDrawerOpen}
        allowOverlayClose={false}
        closeFn={() => handleCancel()}
        style={{ maxWidth: '480px' }}
        mods="u-size12of24"
        showOverlay
      >
        <Panel
          mods="Panel ShareForm"
          className="u-padSidesXl registration-share-form"
        >
          <PanelHeader>
            <h3 data-testid="share-drawer-title">
              Share {formForSharing?.name}
            </h3>

            <div className="Modal-close">
              <Button
                onClick={() => {
                  handleCancel();
                }}
                icon="dismiss"
                mods="u-colorNeutral9"
                style={{ border: 'none' }}
              />
            </div>
          </PanelHeader>

          <PanelBody>
            {isSaveDisabled && errorMessage && errorDescriptor && (
              <PanelRow>
                <div className="share-form-error-container u-spaceEndsMd">
                  <div className="icon-container u-size1of6">
                    <Icon
                      name="alert"
                      mods={'u-fontSizeLg u-colorForeground'}
                    />
                  </div>

                  <div className="error-text-container u-size5of6">
                    <strong>{errorDescriptor}</strong>
                    <br />
                    {errorMessage}
                  </div>
                </div>
              </PanelRow>
            )}

            {((formForSharing && formForSharing.status === 'closed') ||
              status === 'closed') && (
              <PanelRow>
                <div className="info-text-and-svg-wrapper u-spaceEndsMd">
                  <div className="svg-wrapper">
                    <Icon name="info" mods={'u-fontSizeMd u-colorForeground'} />
                  </div>

                  <div className="info-text-wrapper">
                    <p className="info-text">
                      <strong>
                        Registration is not currently open to the public.
                      </strong>
                      <br />
                      Please update the status or open date below to open the
                      form.
                    </p>

                    <p className="info-text">
                      <strong>Status impacts dates.</strong>
                      <br />
                      Changing status from "closed" to "open" will change open
                      date to present time and clear close date if the date is
                      in the past.
                    </p>
                  </div>
                </div>
              </PanelRow>
            )}

            <PanelRow>
              <FieldLabel>Signup Link</FieldLabel>

              <div className="u-flex u-flexJustifyBetween">
                <Field
                  mods="u-flex u-flexJustifyEnd u-flexAlignItemsCenter"
                  name="signup-link"
                  type="input"
                  style={{ marginRight: '10px', width: '100%' }}
                  formFieldProps={{
                    size: 'medium',
                    inputProps: {
                      value: shareUrl,
                      onChange: (e) => null,
                    },
                  }}
                />

                <Button
                  type="button"
                  mods="button u-size1of4"
                  onClick={() => {
                    const cb = navigator.clipboard;
                    cb?.writeText(shareUrl).then(() => {
                      setCopied(true);
                      setTimeout(() => {
                        setCopied(false);
                      }, 1000);
                    });
                  }}
                >
                  <Icon
                    name={copied ? 'task-alt' : 'content-copy'}
                    style={{
                      right: 10,
                      color: copied ? '#199a50' : '#1D2B35',
                    }}
                  />
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </PanelRow>

            <PanelRow>
              <FieldLabel>Status</FieldLabel>

              {!formForSharing && (
                <span>
                  <Loader type="spin" /> Loading...
                </span>
              )}

              {formForSharing && (
                <>
                  <Select
                    name="status"
                    mods="u-spaceBottomSm"
                    options={[
                      {
                        label: 'Open',
                        value: 'open',
                      },
                      {
                        label: 'Closed',
                        value: 'closed',
                      },
                    ]}
                    inputProps={{
                      value: status,
                      onChange: (e) => {
                        handleStatusChange(e.target.value);
                      },
                    }}
                  />

                  <p className="status-helper">
                    Altering status will impact open/close dates.
                  </p>
                </>
              )}
            </PanelRow>

            <PanelRow>
              <FieldLabel>Open Date</FieldLabel>

              {!formForSharing && (
                <span>
                  <Loader type="spin" /> Loading...
                </span>
              )}

              {formForSharing && (
                <EnhancedDateTimePicker
                  testId="open-date-picker"
                  error={!startDate && endDate ? true : false}
                  datetime={startDate}
                  inputClasses=""
                  onChange={handleStartDateChange}
                  onClear={clearStartDate}
                  timezone={formattedTimezone}
                  filterTime={excludeCurrentDayPastTimes}
                  openToDate={startDate ? startDate : new Date()}
                />
              )}
            </PanelRow>

            <PanelRow>
              <FieldLabel>Close Date</FieldLabel>

              {!formForSharing && (
                <span>
                  <Loader type="spin" /> Loading...
                </span>
              )}

              {formForSharing && (
                <EnhancedDateTimePicker
                  testId="close-date-picker"
                  error={
                    endDate &&
                    startDate?.getTime() > endDate.getTime() - oneMinute
                  }
                  datetime={endDate}
                  inputClasses=""
                  onChange={handleEndDateChange}
                  onClear={clearEndDate}
                  timezone={formattedTimezone}
                  filterTime={(dateTime) =>
                    excludeStartDatePastTimes(dateTime, startDate)
                  }
                  startDate={
                    startDate ? excludeDaysUntilStartDate(startDate) : undefined
                  }
                  openToDate={
                    startDate ? excludeDaysUntilStartDate(startDate) : undefined
                  }
                  disabled={!startDate}
                />
              )}
            </PanelRow>

            <PanelRow mods="u-textRight">
              <Button
                style={{
                  backgroundColor: '#f6f8fb',
                  marginRight: 16,
                }}
                onClick={handleCancel}
                mods="u-padSidesXl"
                testId="share-drawer-cancel-button"
              >
                Cancel
              </Button>

              <Button
                isDisabled={isSaveDisabled || isLoading}
                onClick={handleSave}
                color="primary"
                mods="u-padSidesXxl"
                testId="share-drawer-save-button"
              >
                {isLoading ? (
                  <span>
                    <Loader type="spin" /> Loading...
                  </span>
                ) : (
                  'Save'
                )}
              </Button>
            </PanelRow>
          </PanelBody>
        </Panel>
      </Drawer>
    </>
  );
};

export default ShareFormDrawer;
